import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { addToCart } from '../../api/apiStorefront';

/**
 * Sprint B — copy the line items off an expired (auto-cancelled) order back
 * into the customer's active cart and route them to /checkout. Sequential
 * adds keep the server-side stock validation behavior consistent with the
 * normal AddToCartButton flow; we don't short-circuit on the first failure
 * because partial reorder is still useful (out-of-stock items are skipped
 * with a toast, the rest land in the cart).
 */
export const useReorderExpired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isReordering, setIsReordering] = useState(false);

  const reorder = async (order) => {
    const items = Array.isArray(order?.items) ? order.items : [];
    if (items.length === 0) return;
    setIsReordering(true);
    let added = 0;
    let failed = 0;
    for (const item of items) {
      try {
        const payload = { product_id: item.product_id, quantity: item.qty };
        if (item.variant_id) payload.variant_id = item.variant_id;
        await addToCart(payload);
        added += 1;
      } catch {
        failed += 1;
      }
    }
    queryClient.invalidateQueries({ queryKey: ['cart'] });
    queryClient.invalidateQueries({ queryKey: ['cart-count'] });
    setIsReordering(false);
    if (added === 0) {
      toast.error(t('orders.reorderAllFailed', 'Could not add any of these items to your cart.'));
      return;
    }
    if (failed > 0) {
      toast(t('orders.reorderPartial', 'Some items couldn\'t be added (out of stock).'));
    } else {
      toast.success(t('orders.reorderSuccess', 'Items added to your cart.'));
    }
    navigate('/checkout');
  };

  return { reorder, isReordering };
};
